import { useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FloatingSaveBar } from "components/FloatingSaveBar";
import { DateField } from "components/Forms/DateField";
import { EmailArrayField } from "components/Forms/EmailArrayField";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteShipmentQuery,
  Issue,
  IssueLevel,
  ManifestRequestFormat,
  ShippableContainerItemFragment,
  UnitType,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Control, Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  formatQuantity,
  isLocalDateInFuture,
  NO_VALUE_PLACEHOLDER,
  UNIT_LABELS,
} from "../Containers/containerDisplay";
import {
  CANCEL_WASTE_SHIPMENT_MUTATION,
  DELETE_WASTE_SHIPMENT_MUTATION,
  EMAIL_MANIFEST_REQUEST_MUTATION,
  GENERATE_MANIFEST_REQUEST_MUTATION,
  GET_WASTE_SHIPMENT_QUERY,
  MARK_SHIPMENT_REQUESTED_MUTATION,
  MARK_SHIPMENT_SHIPPED_MUTATION,
  SAVE_WASTE_SHIPMENT_MUTATION,
  SHIPMENT_REFETCH_QUERIES,
  SHIPPABLE_CONTAINERS_QUERY,
  VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT,
  VALIDATE_UPDATE_SHIPMENT_LINES_INPUT,
  VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { DeadlineChip } from "./DeadlineChip";
import { isShipmentLevelDiff } from "./reconciliationDisplay";
import { ShipmentContentsCard } from "./ShipmentContentsCard";
import {
  ShipmentDetailSkeleton,
  ShipmentHeader,
  ShipmentInformationCard,
  ShipmentPageGrid,
  ShipmentPageShell,
  ShipmentRail,
} from "./ShipmentDetailChrome";
import {
  EditShipmentFormValues,
  ShipmentDetailsFacts,
  ShipmentDetailsFields,
} from "./ShipmentDetailsCard";
import { formatTotalsByUnit, SAVE_BEFORE_ACTING } from "./shipmentDisplay";
import { formatFromExportUrl } from "./shipmentLifecycle";
import { ShipmentManifestRail } from "./ShipmentManifestRail";
import { shipmentStepActions } from "./shipmentStepActions";
import { DisplayLine, useStagedShipmentLines } from "./useStagedShipmentLines";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

const REFETCH = SHIPMENT_REFETCH_QUERIES;

/**
 * Why the contents cannot be edited, or undefined when they can.
 *
 * An attached manifest freezes them: the manifest is the regulated record of
 * what left the site and its lines are matched to these containers, so editing
 * them afterwards would silently invalidate those links. The server enforces
 * it; this is what lets the UI say so before a save is rejected.
 */
function contentLockReasonFor(shipment: Shipment): string | undefined {
  return shipment.manifestReceivedAt != null
    ? "Detach the manifest to change this shipment's contents."
    : undefined;
}

const isEditable = (status: WasteShipmentStatus) =>
  status === WasteShipmentStatus.Draft || status === WasteShipmentStatus.Requested;

/**
 * The rare and irreversible actions, kept out of the header's main row so the
 * one contained button is unambiguously the next step.
 *
 * Not the shared `ActionsMenu`: that renders `size="large"` and collapses to a
 * plain button when it holds a single item, so it would change size and shape
 * as the shipment moves through its statuses. This stays an icon at the page's
 * small control size.
 */
function ShipmentOverflowMenu({
  shipment,
  hasUnsaved,
  onRequestPickup,
  onAbandon,
}: {
  shipment: Shipment;
  hasUnsaved: boolean;
  onRequestPickup: () => void;
  onAbandon: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  const isDraft = shipment.status === WasteShipmentStatus.Draft;
  const isRequested = shipment.status === WasteShipmentStatus.Requested;
  const exportUrl = shipment.requestExportUrl;

  const items: { key: string; label: string; onClick: () => void; disabled?: boolean }[] = [];
  if (isRequested) {
    items.push({
      key: "resend",
      label: "Send an updated request…",
      onClick: run(onRequestPickup),
      disabled: hasUnsaved,
    });
  }
  if (exportUrl) {
    items.push({
      key: "download",
      label: `Download last request${formatFromExportUrl(exportUrl) ? ` (${formatFromExportUrl(exportUrl)})` : ""}`,
      onClick: run(() => openInNewTab(exportUrl)),
    });
  }
  if (isEditable(shipment.status)) {
    items.push({
      key: "abandon",
      label: isDraft ? "Delete shipment" : "Cancel shipment",
      onClick: run(onAbandon),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More actions"
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        {items.map((item) => (
          <MenuItem key={item.key} onClick={item.onClick} disabled={item.disabled}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The page's commit point, using the same floating bar as chemicals and
 * reports rather than a shape of its own.
 *
 * Cancel leaves edit mode instead of navigating, which is what the bar does by
 * default — here the form IS the page, so going back would take the user off
 * the shipment entirely.
 */
function ShipmentSaveBar({
  unsavedCount,
  issues,
  saving,
  onSave,
  onCancel,
}: {
  unsavedCount: number;
  issues: Issue[];
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const blocked = hasCriticalIssues(issues);
  const dirty = unsavedCount > 0;
  return (
    <FloatingSaveBar
      saving={saving}
      issues={issues}
      notification={
        dirty
          ? `${unsavedCount} unsaved ${pluralize("change", unsavedCount)}`
          : "No unsaved changes"
      }
      saveDisabled={!dirty || blocked}
      saveTooltipTitle={blocked ? "Resolve the issues above first" : ""}
      onSaveClick={onSave}
      onCancel={onCancel}
    />
  );
}

export function WasteShipmentDetail() {
  const { tenantId } = useTenant();
  const { shipmentId } = useParams<{ shipmentId: string }>();

  const { data, loading } = useQuery(GET_WASTE_SHIPMENT_QUERY, {
    variables: { id: shipmentId ?? "" },
    skip: !shipmentId,
  });
  const shipment = data?.wasteShipment ?? null;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    // No placeholder crumb: a generic "Shipment" that swaps to the real number
    // a moment later reads as the page changing its mind. Better to have the
    // trail arrive one step short and then complete.
    ...(shipment ? [{ label: shipment.manifestRequestNumber }] : []),
  ]);

  if (loading && !shipment) {
    return <ShipmentDetailSkeleton />;
  }

  if (!shipment) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Shipment not found</Typography>
      </Box>
    );
  }

  // Keyed on the shipment so a different one starts with clean staged state.
  return <LoadedShipmentDetail key={shipment.id} shipment={shipment} />;
}

/**
 * The shipment page edits as one unit: the header fields and the container
 * changes share a single Save and Discard. Removing a container and thinking
 * better of it should cost nothing, and a shipment should never be
 * half-edited — particularly once its request is in a vendor's hands.
 */
function LoadedShipmentDetail({ shipment }: { shipment: Shipment }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [editLine, setEditLine] = useState<DisplayLine | null>(null);

  const staged = useStagedShipmentLines(shipment);
  const form = useShipmentHeaderForm(shipment);
  const {
    formState: { isDirty: headerDirty, dirtyFields },
    getValues,
    reset,
    issues: headerIssues,
  } = form;

  const [save, { loading: saving }] = useMutation(SAVE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [cancelShipment, { loading: canceling }] = useMutation(CANCEL_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [deleteShipment, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  // Split the comparison by grain. The discriminator for a shipment-level row
  // is the field, NOT a null `wasteStreamId` -- null is also the identity of
  // the uncharacterized bucket, so waste with no stream assigned produces real
  // quantity and code rows that happen to carry a null id.
  const shipmentStreamIds = new Set(
    shipment.lines.map((line) => line.wasteStream?.id).filter((id): id is string => !!id)
  );
  const hasUncharacterizedLines = shipment.lines.some((line) => !line.wasteStream);
  const diffsByStream = new Map<string, typeof shipment.reconciliation.fieldDiffs>();
  const manifestOnlyDiffs: typeof shipment.reconciliation.fieldDiffs = [];
  const shipmentLevelDiffs: typeof shipment.reconciliation.fieldDiffs = [];
  const uncharacterizedDiffs: typeof shipment.reconciliation.fieldDiffs = [];
  for (const diff of shipment.reconciliation.fieldDiffs) {
    if (isShipmentLevelDiff(diff.field)) {
      shipmentLevelDiffs.push(diff);
    } else if (!diff.wasteStreamId) {
      // Uncharacterized waste on one side or both. It belongs with the contents
      // when this shipment actually carried some, and reads as manifest-only
      // when it did not.
      if (hasUncharacterizedLines) {
        uncharacterizedDiffs.push(diff);
      } else {
        manifestOnlyDiffs.push(diff);
      }
    } else if (shipmentStreamIds.has(diff.wasteStreamId)) {
      diffsByStream.set(diff.wasteStreamId, [
        ...(diffsByStream.get(diff.wasteStreamId) ?? []),
        diff,
      ]);
    } else {
      manifestOnlyDiffs.push(diff);
    }
  }

  const canEdit = isEditable(shipment.status);
  // A draft is unfinished by definition, so it opens ready to type in. A
  // requested shipment has already been sent to the vendor, so it opens as a
  // record and asks for a deliberate Edit before it can be changed under one.
  const [editRequested, setEditRequested] = useState(shipment.status === WasteShipmentStatus.Draft);
  const editing = canEdit && editRequested;
  const unsavedCount = Object.keys(dirtyFields).length + staged.changeCount;
  const hasUnsaved = unsavedCount > 0;

  // An attached manifest freezes the contents. The manifest is the regulated
  // record of what left the site, and its lines are matched to these containers
  // — editing them afterwards would silently invalidate those links. The server
  // enforces this; saying so here means the user learns it from the disabled
  // control rather than from a rejected save.
  const contentLockReason = contentLockReasonFor(shipment);

  const handleSave = async () => {
    if (hasCriticalIssues(headerIssues)) {
      return;
    }
    const values = getValues();
    const linesInput = staged.toUpdateLinesInput();
    try {
      // One mutation, one transaction. Sending the header, container and pickup
      // changes separately meant a rule that rejected the second could leave the
      // first committed while the user was told the save failed.
      //
      // Only the sections the user touched are included — presence is what tells
      // the server which parts of the form to apply. In particular the pickup
      // section is sent only when its field is dirty, because a null date there
      // is a real instruction (clear the pickup and release its containers)
      // rather than "unchanged".
      await save({
        variables: {
          input: {
            id: shipment.id,
            ...(headerDirty
              ? {
                  header: toShipmentHeaderFields(values, Boolean(dirtyFields.plannedShipDate)),
                }
              : {}),
            ...(linesInput
              ? {
                  lines: {
                    addContainerIds: linesInput.addContainerIds,
                    removeLineIds: linesInput.removeLineIds,
                    setQuantities: linesInput.setQuantities,
                  },
                }
              : {}),
            ...(dirtyFields.scheduledPickupDate
              ? {
                  pickup: {
                    scheduledPickupDate: values.scheduledPickupDate
                      ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.scheduledPickupDate))
                      : null,
                  },
                }
              : {}),
          },
        },
      });
      staged.reset();
      // The header form keeps dirty values across a reset so the refetch can't
      // clobber someone mid-edit — which also means a successful save leaves it
      // looking unsaved. Rebase it on what was just persisted, or the page goes
      // on claiming changes and "Mark shipped" stays disabled until the user
      // discards an edit that already landed.
      reset(values);
      alerts.success("Shipment updated.");
    } catch (e) {
      // Nothing to unwind on the client: the save is one transaction, so a
      // failure means nothing persisted and every edit is still pending. The
      // form keeps its dirty state, which is now the truth rather than a guess.
      alerts.error(e instanceof Error ? e.message : "Failed to update the shipment.");
    }
  };

  const handleDiscard = () => {
    reset();
    staged.reset();
  };

  // Cancel throws away whatever is staged and drops back to the record. A draft
  // opens in edit mode again on the next load, which is the state a draft
  // should be in.
  const handleCancelEdit = () => {
    handleDiscard();
    setEditRequested(false);
  };

  // A draft never left the building, so it is deleted rather than left behind
  // as a canceled row; once requested, the vendor has its number and the
  // record stays.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const handleAbandon = async () => {
    try {
      if (isDraft) {
        await deleteShipment({ variables: { id: shipment.id } });
        alerts.success(`Shipment ${shipment.manifestRequestNumber} deleted.`);
        setCancelOpen(false);
        navigate(`/o/${tenantId}/waste/shipments`);
        return;
      }
      await cancelShipment({ variables: { id: shipment.id } });
      alerts.success(`Shipment ${shipment.manifestRequestNumber} canceled.`);
      setCancelOpen(false);
    } catch (e) {
      alerts.error(
        e instanceof Error ? e.message : `Failed to ${isDraft ? "delete" : "cancel"} the shipment.`
      );
    }
  };

  return (
    <ShipmentPageShell>
      <ShipmentHeader
        shipment={shipment}
        issues={shipment.issues}
        requestStale={canEdit && shipment.manifestRequestStale}
        resendBlockedReason={hasUnsaved ? SAVE_BEFORE_ACTING : undefined}
        onResendRequest={canEdit ? () => setRequestOpen(true) : undefined}
        actions={
          canEdit ? (
            <>
              {/* Only while reading: in edit mode the save bar carries the
                  issue list, and two of them a screen apart is the duplication
                  this layout exists to remove. */}
              {!editing && <IssueListButton issues={headerIssues} />}
              {!editing && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setEditRequested(true)}
                >
                  Edit
                </Button>
              )}
            </>
          ) : undefined
        }
        overflow={
          <ShipmentOverflowMenu
            shipment={shipment}
            hasUnsaved={hasUnsaved}
            onRequestPickup={() => setRequestOpen(true)}
            onAbandon={() => setCancelOpen(true)}
          />
        }
      />

      <ShipmentInformationCard shipment={shipment} />

      <ShipmentPageGrid
        body={
          <>
            {editing ? (
              <ShipmentDetailsFields form={form} />
            ) : (
              <ShipmentDetailsFacts shipment={shipment} />
            )}
            <ShipmentContentsCard
              shipment={shipment}
              lines={staged.displayLines}
              editable={editing}
              contentLockReason={contentLockReason}
              issues={shipment.issues}
              onAddContainers={() => setAddOpen(true)}
              onEditLine={setEditLine}
              onRemoveLine={staged.removeLine}
              onRestoreLine={staged.restoreLine}
              diffsByStream={diffsByStream}
              manifestOnlyDiffs={manifestOnlyDiffs}
              shipmentLevelDiffs={shipmentLevelDiffs}
              uncharacterizedDiffs={uncharacterizedDiffs}
            />
          </>
        }
        rail={
          <ShipmentRail
            shipment={shipment}
            manifest={<ShipmentManifestRail shipment={shipment} />}
            stepActions={shipmentStepActions({
              canEdit,
              isDraft,
              hasUnsaved,
              onRequestPickup: () => setRequestOpen(true),
              onMarkShipped: () => setShipOpen(true),
            })}
          />
        }
      />

      {editing && (
        <ShipmentSaveBar
          unsavedCount={unsavedCount}
          issues={headerIssues}
          saving={saving}
          onSave={handleSave}
          onCancel={handleCancelEdit}
        />
      )}

      {addOpen && (
        <AddContainersDialog
          shipment={shipment}
          stagedContainerIds={staged.stagedContainerIds}
          onStage={staged.addContainers}
          onClose={() => setAddOpen(false)}
        />
      )}
      {shipOpen && (
        <MarkShipmentShippedDialog shipment={shipment} onClose={() => setShipOpen(false)} />
      )}
      {requestOpen && (
        <RequestPickupDialog shipment={shipment} onClose={() => setRequestOpen(false)} />
      )}
      {editLine?.lineId && (
        <EditLineQuantityDialog
          shipmentId={shipment.id}
          line={{ ...editLine, lineId: editLine.lineId }}
          onStage={staged.setQuantity}
          onClose={() => setEditLine(null)}
        />
      )}
      {cancelOpen && (
        <Dialog
          open
          onClose={() => setCancelOpen(false)}
          maxWidth="xs"
          title={isDraft ? "Delete this shipment?" : "Cancel this shipment?"}
          primaryActionButtonProps={{
            saveText: isDraft ? "Delete shipment" : "Cancel shipment",
            loading: canceling || deleting,
            onClick: handleAbandon,
          }}
        >
          <Typography variant="body2">
            {isDraft
              ? `${shipment.manifestRequestNumber} has not gone to a vendor, so it will be deleted outright. Its containers are untouched and stay available to package.`
              : `${shipment.manifestRequestNumber} will be marked canceled and kept, because the vendor already has its request. No container amounts change — removals only happen when a shipment ships.`}
          </Typography>
        </Dialog>
      )}
    </ShipmentPageShell>
  );
}

/**
 * PDF leads: it is the one a vendor can print and file as-is, and the
 * spreadsheets exist for anyone who wants to work the numbers.
 */
const REQUEST_FORMAT_OPTIONS: { value: ManifestRequestFormat; label: string }[] = [
  { value: ManifestRequestFormat.Pdf, label: "PDF" },
  { value: ManifestRequestFormat.Csv, label: "CSV" },
  { value: ManifestRequestFormat.Xlsx, label: "Excel (XLSX)" },
];

/** Open a signed URL without navigating away from the shipment. */
function openInNewTab(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();
}

/**
 * The vendor-facing half of the shipment: produce the manifest request, send
 * it, or record that it was arranged some other way.
 *
 * All three actions move a draft to Requested, which is why they live together
 * — the request is a single milestone reached by three routes, not three
 * separate states.
 */
/** How the vendor is told about the pickup. One decision, three routes. */
type RequestRoute = "email" | "download" | "elsewhere";

/** The confirm button names the route's outcome, so it is keyed off the route. */
const PRIMARY_ACTION_LABEL: Record<RequestRoute, string> = {
  email: "Send request",
  download: "Download request",
  elsewhere: "Mark as requested",
};

type RequestPickupFormValues = {
  route: RequestRoute;
  format: ManifestRequestFormat;
  recipients: string[];
};

/** Rows in the vendor preview are one stream per unit, keyed the same way. */
function streamRowKey(line: Shipment["manifestRequestLines"][number]): string {
  return line.wasteStream?.id ?? "uncharacterized";
}

/**
 * The format choice belongs to the route that produces a document, so it
 * renders indented under whichever of those routes is selected rather than as
 * a dialog-wide setting.
 */
function RequestFormatField({ control }: { control: Control<RequestPickupFormValues> }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Controller
        control={control}
        name="format"
        render={({ field }) => (
          <TextField {...field} select size="small" label="Format" sx={{ width: 180 }}>
            {REQUEST_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Typography variant="caption" color="text.secondary">
        PDF to print or forward · CSV and Excel to work the numbers
      </Typography>
    </Stack>
  );
}

/**
 * One dialog for the whole "tell the vendor" step.
 *
 * Generating a document, emailing it, and recording a request made by phone are
 * not three separate actions — they are three answers to "how do you want to
 * send it", so they are one radio rather than three buttons competing on the
 * page. The shipment lands in REQUESTED either way.
 *
 * It leads with what the vendor will actually receive: before this, the only
 * way to know was to generate a file and open it.
 */
function RequestPickupDialog({ shipment, onClose }: { shipment: Shipment; onClose: () => void }) {
  const alerts = useAlerts();
  const client = useApolloClient();

  const form = useForm<RequestPickupFormValues>({
    defaultValues: {
      route: "email",
      format: ManifestRequestFormat.Pdf,
      recipients: [],
    },
  });
  const { control, getValues, watch, setError } = form;
  const route = watch("route");

  const [generate, { loading: generating }] = useMutation(GENERATE_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [emailRequest, { loading: emailing }] = useMutation(EMAIL_MANIFEST_REQUEST_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [markRequested, { loading: marking }] = useMutation(MARK_SHIPMENT_REQUESTED_MUTATION, {
    refetchQueries: REFETCH,
  });
  const loading = generating || emailing || marking;

  const primaryLabel = PRIMARY_ACTION_LABEL[route];

  // Lines are grouped by stream, unit, and snapshot signature. A stream
  // stored in two units renders as two rows; label those with the unit so
  // they don't read as duplicates. Signature-only splits keep the bare name —
  // the unit is not what distinguishes them.
  const streamUnits = new Map<string, Set<string>>();
  for (const line of shipment.manifestRequestLines) {
    const key = streamRowKey(line);
    const units = streamUnits.get(key) ?? new Set<string>();
    units.add(line.unit);
    streamUnits.set(key, units);
  }

  const handleConfirm = async () => {
    if (loading) {
      return;
    }
    const values = getValues();
    // Validated on submit rather than by disabling the button: a disabled
    // button can't say why it is disabled, and it is unreachable to keyboard
    // and screen-reader users.
    if (values.route === "email" && values.recipients.length === 0) {
      setError("recipients", {
        type: "required",
        message: "Add at least one recipient to send the request.",
      });
      return;
    }
    try {
      if (values.route === "email") {
        await emailRequest({
          variables: {
            shipmentId: shipment.id,
            format: values.format,
            recipients: values.recipients,
          },
        });
        alerts.success(
          `Manifest request ${shipment.manifestRequestNumber} sent to ${values.recipients.length} ${
            values.recipients.length === 1 ? "recipient" : "recipients"
          }.`
        );
      } else if (values.route === "download") {
        const { data } = await generate({
          variables: { shipmentId: shipment.id, format: values.format },
        });
        const url = data?.generateManifestRequest.url;
        if (url) {
          openInNewTab(url);
        }
        alerts.success(`Manifest request generated for ${shipment.manifestRequestNumber}.`);
      } else {
        await markRequested({ variables: { shipmentId: shipment.id } });
        alerts.success(`${shipment.manifestRequestNumber} is marked requested.`);
      }
      onClose();
    } catch (e) {
      // Emailing is not all-or-nothing: the shipment flips to REQUESTED on the
      // FIRST recipient that succeeds, so a failure on a later one leaves the
      // server ahead of this page. Apollo runs refetchQueries only on success,
      // so without this the user is looking at DRAFT for a shipment that is
      // already requested, and only a reload would tell them otherwise.
      await client.refetchQueries({ include: REFETCH }).catch(() => {
        // The error below is the useful one; a failed re-sync must not replace it.
      });
      alerts.error(e instanceof Error ? e.message : "Failed to request the pickup.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Request pickup for ${shipment.manifestRequestNumber}`}
      primaryActionButtonProps={{
        saveText: primaryLabel,
        loading,
        disabled: loading,
        onClick: handleConfirm,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your vendor gets this shipment summarized by waste stream. It is not an official
            manifest, so they create that and send it back.
          </Typography>

          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                What the vendor sees
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formatTotalsByUnit(shipment.totalQuantityByUnit)}
              </Typography>
            </Stack>
            {shipment.manifestRequestLines.map((line, i) => (
              <Stack
                key={`${line.wasteStream?.id ?? "uncharacterized"}-${line.unit}-${i}`}
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {line.wasteStream?.name ?? "Uncharacterized"}
                  {(streamUnits.get(streamRowKey(line))?.size ?? 0) > 1 &&
                    ` (${UNIT_LABELS[line.unit].toLowerCase()})`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {line.containerCount} {line.containerCount === 1 ? "container" : "containers"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatQuantity(line.quantity, line.unit)}
                </Typography>
              </Stack>
            ))}
            <Typography variant="caption" color="text.secondary">
              Plus a per-container appendix for your records.
            </Typography>
          </Paper>

          <Controller
            control={control}
            name="route"
            render={({ field }) => (
              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 0.5 }}>How do you want to send it?</FormLabel>
                <RadioGroup {...field} sx={{ gap: 0.75 }}>
                  <FormControlLabel
                    value="email"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">Email it to the vendor now</Typography>
                        <Typography variant="caption" color="text.secondary">
                          They get the file attached, so nothing expires.
                        </Typography>
                      </Box>
                    }
                  />
                  {route === "email" && (
                    <Stack spacing={2} sx={{ pl: 4, pt: 1, pb: 1 }}>
                      <EmailArrayField name="recipients" label="Vendor recipients" />
                      <RequestFormatField control={control} />
                    </Stack>
                  )}
                  <FormControlLabel
                    value="download"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">Download it and send it myself</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Marks the shipment requested once the file is created.
                        </Typography>
                      </Box>
                    }
                  />
                  {route === "download" && (
                    <Box sx={{ pl: 4, pt: 0.5, pb: 1 }}>
                      <RequestFormatField control={control} />
                    </Box>
                  )}
                  <FormControlLabel
                    value="elsewhere"
                    sx={{ alignItems: "flex-start" }}
                    control={<Radio size="small" sx={{ pt: 0.25 }} />}
                    label={
                      <Box>
                        <Typography variant="body2">I already requested it another way</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Phoned in, or entered in the vendor&apos;s own portal. No document is
                          created.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {shipment.status === WasteShipmentStatus.Draft && (
            <Alert severity="info">
              This moves {shipment.manifestRequestNumber} to Requested. You can still edit it
              afterwards.
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
function toUpdateShipmentInput(
  shipmentId: string,
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return { id: shipmentId, ...toShipmentHeaderFields(values, plannedShipDateDirty) };
}

/**
 * The header fields alone, shared by the validator query (which wants them
 * under an id) and the combined Save's `header` section (which does not), so
 * the two can never normalize a value differently.
 */
function toShipmentHeaderFields(values: EditShipmentFormValues, plannedShipDateDirty: boolean) {
  return {
    vendorId: values.vendorId,
    transporterName: values.transporterName.trim() || null,
    destinationName: values.destinationName.trim() || null,
    destinationEpaId: values.destinationEpaId.trim() || null,
    // Only re-normalize the date when the user actually touched it, so an
    // untouched value can't shift a day through the local timezone.
    ...(plannedShipDateDirty
      ? {
          plannedShipDate: values.plannedShipDate
            ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.plannedShipDate))
            : null,
        }
      : {}),
  };
}

/**
 * Async header-edit validation; the seam future field checks land in. The
 * validation payload always includes the date (nothing persists from it, so
 * the dirty-gated timezone guard only matters on the real mutation input).
 */
function useShipmentUpdateValidation(shipmentId: string) {
  const [query] = useLazyQuery(VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EditShipmentFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: { input: toUpdateShipmentInput(shipmentId, values, true) },
      });
      return data?.wasteShipmentUpdateInputValidator ?? [];
    },
    [query, shipmentId]
  );
}

/**
 * The shipment's header as a live inline form — the single place vendor,
 * transporter, TSDF, and dates are collected. Save/Discard appear only when
 * dirty; background refetches keep in-progress edits (keepDirtyValuesOnReset).
 * 3C note: once requests generate, a header edit after requestGeneratedAt
 * should surface a stale-request warning here.
 */
/** The header form, owned by the page so one Save can cover the whole thing. */
function useShipmentHeaderForm(shipment: Shipment) {
  const defaultValues = useMemo<EditShipmentFormValues>(
    () => ({
      vendorId: shipment.vendor?.id ?? null,
      transporterName: shipment.transporterName ?? "",
      destinationName: shipment.destinationName ?? "",
      destinationEpaId: shipment.destinationEpaId ?? "",
      plannedShipDate: storedDateToDateOnly(shipment.plannedShipDate),
      scheduledPickupDate: storedDateToDateOnly(shipment.scheduledPickupDate),
    }),
    [shipment]
  );

  return useValidatingForm<EditShipmentFormValues>(
    defaultValues,
    [],
    useShipmentUpdateValidation(shipment.id),
    [],
    { keepDirtyValuesOnReset: true }
  );
}

function AddContainersDialog({
  shipment,
  stagedContainerIds,
  onStage,
  onClose,
}: {
  shipment: Shipment;
  /** Containers already on the shipment or already staged. */
  stagedContainerIds: string[];
  onStage: (containers: ShippableContainerItemFragment[]) => void;
  onClose: () => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: shipment.facilityId },
    fetchPolicy: "cache-and-network",
  });
  const onShipment = useMemo(() => new Set(stagedContainerIds), [stagedContainerIds]);
  const candidates = useMemo(
    () => (data?.facility?.shippableContainers?.items ?? []).filter((c) => !onShipment.has(c.id)),
    [data?.facility?.shippableContainers?.items, onShipment]
  );

  // Live warn-level validation as the selection changes — e.g. a container
  // already on another open shipment warns here just like in the builder.
  const [validate, { data: validationData }] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    if (selectedIds.size > 0) {
      validate({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
    }
  }, [selectedIds, shipment.id, validate]);
  const issues: Issue[] =
    selectedIds.size > 0 ? (validationData?.updateShipmentLinesInputValidator ?? []) : [];

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Staged, not saved: the page has one Save, and adding a container should be
  // as undoable as any other edit on it.
  const handleAdd = () => {
    onStage(candidates.filter((c) => selectedIds.has(c.id)));
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title="Add containers"
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Add",
        disabled: selectedIds.size === 0 || hasCriticalIssues(issues),
        onClick: handleAdd,
      }}
    >
      {loading && candidates.length === 0 && <Skeleton height={120} />}
      {!loading && candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Every shippable container at this facility is already on the shipment.
        </Typography>
      )}
      <Stack sx={{ maxHeight: 360, overflowY: "auto" }}>
        {candidates.map((container) => (
          <Stack key={container.id} direction="row" alignItems="center" spacing={1.5}>
            <Checkbox
              size="small"
              checked={selectedIds.has(container.id)}
              onChange={() => toggle(container.id)}
              inputProps={{ "aria-label": `Select ${container.containerLabel}` }}
            />
            <Typography variant="subtitle2" sx={{ minWidth: 90 }}>
              {container.containerLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }}>
              {container.wasteStream?.name ?? "Uncharacterized"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
              {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
            </Typography>
            <Typography variant="body2">
              {container.amount != null && container.amountUnit
                ? formatQuantity(container.amount, container.amountUnit)
                : NO_VALUE_PLACEHOLDER}
            </Typography>
            {/* The deadline is what decides which drums go on a shipment, so
                this dialog shows it the same way the packaging wizard does —
                the two are the same decision made from different entry
                points. */}
            <DeadlineChip deadline={container.clock.effectiveDeadlineDate} />
          </Stack>
        ))}
      </Stack>
      {issues.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {issues.map((issue, i) => (
            <Alert
              key={`${issue.key}-${i}`}
              severity={issue.level === "CRITICAL" ? "error" : "warning"}
            >
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}

/**
 * The validator's input types the quantity as a required Float, so a value that
 * isn't a number can't be sent at all — it serializes to null and the query
 * errors, which would otherwise surface as "no issues" and leave Save enabled.
 */
const UNPARSEABLE_QUANTITY_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "lineQuantityInvalid",
  message: "Enter the quantity as a number.",
  modelName: "WasteShipmentLine",
  dataName: null,
  jurisdictions: [],
};

type EditLineQuantityFormValues = {
  quantity: string;
  unit: UnitType;
};

function EditLineQuantityDialog({
  shipmentId,
  line,
  onStage,
  onClose,
}: {
  shipmentId: string;
  line: DisplayLine & { lineId: string };
  onStage: (lineId: string, quantity: number, unit: UnitType) => void;
  onClose: () => void;
}) {
  const defaultValues = useMemo<EditLineQuantityFormValues>(
    () => ({ quantity: String(line.quantity), unit: line.unit }),
    [line]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: EditLineQuantityFormValues): Promise<Issue[]> => {
      const quantity = safeParseFloat(values.quantity);
      if (quantity === null) {
        return [UNPARSEABLE_QUANTITY_ISSUE];
      }
      const { data } = await validateQuery({
        variables: {
          input: {
            shipmentId,
            setQuantities: [{ lineId: line.lineId, quantity, unit: values.unit }],
          },
        },
      });
      return data?.updateShipmentLinesInputValidator ?? [];
    },
    [validateQuery, shipmentId, line.lineId]
  );

  const form = useValidatingForm<EditLineQuantityFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    watch,
    formState: { isDirty },
    issues,
  } = form;
  const quantityValue = watch("quantity");
  const unitValue = watch("unit");

  const handleSave = () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    const values = getValues();
    const quantity = safeParseFloat(values.quantity);
    if (quantity === null) {
      return;
    }
    onStage(line.lineId, quantity, values.unit);
    onClose();
  };

  // Say it on the field, not only behind the issue button: the number the
  // reader just typed is where the objection belongs.
  const sameUnitCap =
    unitValue === line.container.amountUnit && line.container.amount != null
      ? line.container.amount
      : null;
  const typed = safeParseFloat(quantityValue);
  const overContainer = sameUnitCap != null && typed != null && typed > sameUnitCap;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={`Quantity for ${line.container.containerLabel}`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Apply",
        disabled: hasCriticalIssues(issues) || !isDirty || safeParseFloat(quantityValue) === null,
        onClick: handleSave,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ship less than the full container (partial ship) or set the amount leaving a tank.
          </Typography>
          <FormTextField
            control={control}
            name="quantity"
            label="Quantity"
            fullWidth
            type="number"
            // Capped only when the units agree. Across units the container's
            // amount is not a bound this field can state -- that comparison
            // needs the stream's density, which the server does.
            inputProps={{
              min: 0,
              step: "any",
              ...(sameUnitCap != null ? { max: sameUnitCap } : {}),
            }}
            helperText={
              overContainer
                ? `More than the container holds (${formatQuantity(line.container.amount, line.container.amountUnit)})`
                : `Container holds ${formatQuantity(line.container.amount, line.container.amountUnit)}`
            }
            textFieldProps={{ size: "small", error: overContainer }}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <TextField
                label="Unit"
                size="small"
                select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as UnitType)}
              >
                {Object.values(UnitType).map((u) => (
                  <MenuItem key={u} value={u}>
                    {UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
}

/**
 * The ship dialog's starting date: the planned ship date when there is one and
 * it has arrived, otherwise today. A planned date still in the future would be
 * rejected as a future shipped date, so it is not offered.
 */
function plannedShippedDateDefault(plannedShipDate: string | null | undefined): string | null {
  const today = DateTime.now().startOf("day");
  const plannedDay = storedDateToDateOnly(plannedShipDate);
  if (plannedDay) {
    const planned = DateTime.fromISO(plannedDay).startOf("day");
    if (planned.isValid && planned <= today) {
      return planned.toISODate();
    }
  }
  return today.toISODate();
}

/**
 * A stored shipment date is UTC midnight standing for a calendar day, but the
 * date picker parses what it is given in the browser's zone — so handing it the
 * raw instant renders the day before for anyone behind UTC. Read the day out in
 * UTC and pass a plain date, which the picker shows verbatim.
 *
 * Done here rather than in DateField: that component is shared by 26 call
 * sites, some of which mean a real instant.
 */
function storedDateToDateOnly(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
}

type ShipFormValues = { shippedDate: string | null };

/**
 * Mirrors the server rule's key and message: the input serializes the picked
 * date to UTC midnight, so the server can no longer tell which calendar day the
 * user was on and keeps a looser end-of-day tolerance. Deciding it here — as the
 * container fill dialogs do — is what makes "tomorrow" wrong for a user whose
 * today is already the server's tomorrow.
 */
const SHIPPED_DATE_IN_FUTURE_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "shippedDateInFuture",
  message: "The shipped date cannot be in the future.",
  modelName: "WasteShipment",
  dataName: null,
  jurisdictions: [],
};

function toShipInput(shipmentId: string, values: ShipFormValues) {
  return {
    shipmentId,
    shippedDate: values.shippedDate
      ? (toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.shippedDate)) ?? null)
      : null,
  };
}

/**
 * The commit step, not a second editor. What is on the shipment and how much
 * of each container leaves are decisions made on the shipment page, so this
 * confirms the date and shows the one thing the page cannot: what shipping
 * will actually do to each container, and whether anything blocks it.
 */
function MarkShipmentShippedDialog({
  shipment,
  onClose,
}: {
  shipment: Shipment;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<ShipFormValues>(
    () => ({
      // The planned date is what the user already told us this shipment was
      // going out on, so it beats a guess. Falls back to today when the
      // shipment was never planned, or when the plan is still in the future
      // and the waste is leaving now.
      shippedDate: plannedShippedDateDefault(shipment.plannedShipDate),
    }),
    [shipment.plannedShipDate]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: ShipFormValues): Promise<Issue[]> => {
      const input = toShipInput(shipment.id, values);
      if (!input.shippedDate) {
        return [];
      }
      if (isLocalDateInFuture(values.shippedDate)) {
        return [SHIPPED_DATE_IN_FUTURE_ISSUE];
      }
      const { data } = await validateQuery({ variables: { input } });
      return data?.markShipmentShippedInputValidator ?? [];
    },
    [validateQuery, shipment.id]
  );

  // Seeded from the shipment's own standing issues so a blocked shipment says
  // so the moment this opens, without waiting on a round-trip.
  const form = useValidatingForm<ShipFormValues>(defaultValues, shipment.issues, validateInput, []);
  const { control, getValues, watch, issues } = form;

  const [markShipped, { loading }] = useMutation(MARK_SHIPMENT_SHIPPED_MUTATION, {
    refetchQueries: REFETCH,
  });

  const shippedDate = watch("shippedDate");
  const blocked = hasCriticalIssues(issues) || !shippedDate || loading;
  const blockers = issues.filter((issue) => issue.level === IssueLevel.Critical);
  const preview = shipment.shipPreview;

  const effects = [
    preview.archivedCount > 0
      ? `archive ${preview.archivedCount} one-way ${preview.archivedCount === 1 ? "container" : "containers"}`
      : null,
    preview.emptiedTankCount > 0
      ? `empty ${preview.emptiedTankCount} ${preview.emptiedTankCount === 1 ? "tank" : "tanks"} and return ${preview.emptiedTankCount === 1 ? "it" : "them"} to service`
      : null,
  ].filter((effect): effect is string => effect !== null);

  const handleShip = async () => {
    if (blocked) {
      return;
    }
    const input = toShipInput(shipment.id, getValues());
    if (!input.shippedDate) {
      return;
    }
    try {
      await markShipped({ variables: { input } });
      alerts.success(`${shipment.manifestRequestNumber} is marked shipped.`);
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to mark the shipment shipped.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Mark ${shipment.manifestRequestNumber} shipped`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Mark shipped",
        loading,
        disabled: blocked,
        onClick: handleShip,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This records {formatTotalsByUnit(shipment.totalQuantityByUnit)} across{" "}
            {shipment.lines.length} {shipment.lines.length === 1 ? "container" : "containers"} as
            having left the site. To change what is on the shipment, or how much of a container
            leaves, close this and edit its contents.
          </Typography>

          <Box>
            <DateField
              control={control}
              name="shippedDate"
              label="Shipped date"
              datePickerProps={{
                disableFuture: true,
                slotProps: { textField: { size: "small", sx: { width: 220 } } },
              }}
            />
          </Box>

          {blockers.length > 0 ? (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {blockers.length} {blockers.length === 1 ? "problem" : "problems"} to fix before
                this can ship
              </Typography>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.25}>
                {blockers.map((issue, i) => (
                  <Typography key={`${issue.key}-${i}`} component="li" variant="caption">
                    {issue.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Shipping this will
              </Typography>
              <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.25}>
                {effects.map((effect) => (
                  <Typography key={effect} component="li" variant="body2" color="text.secondary">
                    {effect}
                  </Typography>
                ))}
                {preview.keepsBalance.map((balance) => (
                  <Typography
                    key={balance.containerLabel}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                  >
                    leave {balance.containerLabel} on site with{" "}
                    {formatQuantity(balance.remaining, balance.unit)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {issues
            .filter((issue) => issue.level !== IssueLevel.Critical)
            .map((issue, i) => (
              <Alert key={`${issue.key}-${i}`} severity="warning">
                {issue.message}
              </Alert>
            ))}

          {!shipment.manifestReceivedAt && (
            <Alert severity="info">
              No manifest is attached yet. That&apos;s fine — the hauler usually leaves one at
              pickup, and you can attach it later.
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
